import { z } from "zod";
import type { Keyset } from "./envelope";
import { LODGING_BLOCK_MAX, LODGING_ROOM_NO_MAX } from "./accommodation";
import { PAGE_SIZE_DEFAULT, PAGE_SIZE_MAX } from "./pagination";
/**
 * Helpdesk (M11): one queue of tickets with categories. ROOM is the one M11
 * routes (a building is required); M13 routes the others. Wire shapes only
 * (snake_case). Plan: `~/.claude/plans/m11-accommodation/PLAN.md` §2.1, §3.
 *
 * The database CHECKs (migration `0025_accommodation`) are generated from the
 * tuples below, and the status trigger `fn_helpdesk_status_guard` encodes
 * `HELPDESK_TRANSITIONS` — a unit test holds them equal. Notes are
 * append-only (a trigger blocks UPDATE / DELETE). No photos and no SLA in M11.
 */

// ── Vocabulary ──────────────────────────────────────────────────────────────

/** `helpdesk_tickets.category` (`ck_helpdesk_tickets_category`). ROOM ⇒ building set. */
export const HELPDESK_CATEGORIES = ["ROOM", "TRANSPORT", "MEALS", "CARD", "HEALTH", "SAFETY", "OTHER"] as const;
export type HelpdeskCategory = (typeof HELPDESK_CATEGORIES)[number];

/** `helpdesk_tickets.priority` (`ck_helpdesk_tickets_priority`). */
export const HELPDESK_PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"] as const;
export type HelpdeskPriority = (typeof HELPDESK_PRIORITIES)[number];
export const HELPDESK_PRIORITY_DEFAULT: HelpdeskPriority = "MEDIUM";

/**
 * `helpdesk_tickets.priority_rank` (GENERATED STORED): lower = more urgent,
 * so the priority sort is ascending and matches `idx_helpdesk_open_priority`.
 */
export const HELPDESK_PRIORITY_RANK = { URGENT: 1, HIGH: 2, MEDIUM: 3, LOW: 4 } as const satisfies Record<HelpdeskPriority, number>;

/** `helpdesk_tickets.status` (`ck_helpdesk_tickets_status`). */
export const HELPDESK_STATUSES = ["OPEN", "IN_PROGRESS", "RESOLVED"] as const;
export type HelpdeskStatus = (typeof HELPDESK_STATUSES)[number];
/** Not resolved — the list's default filter and the partial indexes' predicate (`status <> 'RESOLVED'`). */
export const HELPDESK_OPEN_STATUSES = ["OPEN", "IN_PROGRESS"] as const satisfies readonly HelpdeskStatus[];

/** Where a ticket came from (`ck_helpdesk_tickets_source`). PARTICIPANT is for M13. */
export const HELPDESK_SOURCES = ["DESK", "NOC", "HOTEL", "PARTICIPANT"] as const;
export type HelpdeskSource = (typeof HELPDESK_SOURCES)[number];

export interface HelpdeskTransition {
	from: HelpdeskStatus;
	to: HelpdeskStatus;
}

/**
 * The status graph: OPEN ↔ IN_PROGRESS, either → RESOLVED, RESOLVED → OPEN
 * (reopen). `fn_helpdesk_status_guard` refuses any other move with 23514
 * `ck_helpdesk_tickets_status_transition` (→ INVALID_STATUS_TRANSITION) and
 * sets / clears `resolved_at`. Change both in one migration.
 */
export const HELPDESK_TRANSITIONS: readonly HelpdeskTransition[] = [
	{ from: "OPEN", to: "IN_PROGRESS" },
	{ from: "IN_PROGRESS", to: "OPEN" },
	{ from: "OPEN", to: "RESOLVED" },
	{ from: "IN_PROGRESS", to: "RESOLVED" },
	{ from: "RESOLVED", to: "OPEN" },
];

export const helpdeskTransitionAllowed = (from: HelpdeskStatus, to: HelpdeskStatus): boolean =>
	HELPDESK_TRANSITIONS.some((t) => t.from === from && t.to === to);
export const helpdeskNextStatuses = (from: HelpdeskStatus): HelpdeskStatus[] => HELPDESK_TRANSITIONS.filter((t) => t.from === from).map((t) => t.to);

/** List sorts: newest first (`created_at, ticket_id` DESC) or priority first (`priority_rank` ASC, then newest). */
export const HELPDESK_SORT_KEYS = ["newest", "priority"] as const;
export type HelpdeskSortKey = (typeof HELPDESK_SORT_KEYS)[number];

// ── Limits ──────────────────────────────────────────────────────────────────

export const HELPDESK_TITLE_MAX = 120;
export const HELPDESK_DETAILS_MAX = 2000;
export const HELPDESK_NOTE_MAX = 2000;
export const HELPDESK_CONTACT_NAME_MAX = 120;
export const HELPDESK_CONTACT_PHONE_MAX = 30;
/** Notes a ticket detail returns (`json_agg … LIMIT`). */
export const HELPDESK_NOTES_MAX = 200;
export const HELPDESK_SEARCH_MAX = 100;
/** The keyset cursor: Postgres' full-precision text, never a JS Date (bug 6). */
export const HELPDESK_CURSOR_MAX = 300;

// ── Formatting ──────────────────────────────────────────────────────────────

/** `HD-000123`: a ticket's `ticket_no` (from `helpdesk_ticket_no_seq`) as printed everywhere. */
export const formatTicketNo = (ticketNo: number | string): string => `HD-${String(ticketNo).padStart(6, "0")}`;

/** `HD-000123`, `hd-123`, `000123`, `123` → 123. Anything else → null (the search box then tries text). */
export function parseTicketNoQuery(q: string): number | null {
	const m = /^(?:HD-?)?0*([0-9]{1,12})$/i.exec(q.trim());
	if (!m) return null;
	const n = Number(m[1]);
	return Number.isSafeInteger(n) && n > 0 ? n : null;
}

// ── Messages (message-factory pattern, web namespace `helpdesk`) ────────────

export interface HelpdeskFormMessages {
	required: string;
	textTooLong: string;
	titleRequired: string;
	bodyRequired: string;
	/** A ROOM ticket with no building, or a room with no building. */
	buildingRequired: string;
	/** Both a picked room and a typed room number. */
	roomBothGiven: string;
	nothingToChange: string;
}

export const enHelpdeskFormMessages: HelpdeskFormMessages = {
	required: "Required",
	textTooLong: "Too long",
	titleRequired: "Say what the problem is",
	bodyRequired: "Write a note",
	buildingRequired: "Pick the building",
	roomBothGiven: "Pick a room or type its number, not both",
	nothingToChange: "Nothing to change",
};

type HM = HelpdeskFormMessages;

const uuid = z.string().uuid();
/** Optional text: trimmed, then ≤ max; blank, null or omitted → null. */
const nullableText = (m: HM, max: number) =>
	z
		.string()
		.trim()
		.max(max, m.textTooLong)
		.nullable()
		.optional()
		.transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** `a,b,c` off the query string → a de-duplicated list of `values`. */
const enumList = <T extends readonly [string, ...string[]]>(values: T) =>
	z
		.string()
		.max(200)
		.transform((v) =>
			v
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s.length > 0),
		)
		.pipe(z.array(z.enum(values)).min(1))
		.transform((list) => [...new Set(list)]);

// ── Requests ────────────────────────────────────────────────────────────────

/** `/helpdesk/tickets/:id`. */
export const helpdeskTicketParam = z.object({ id: uuid });

/**
 * `GET /helpdesk/tickets` (S2) — keyset on `(created_at, helpdesk_ticket_id)
 * DESC` (`newest`) or `(priority_rank ASC, created_at DESC, …)`
 * (`priority`); `status` omitted = HELPDESK_OPEN_STATUSES. The first page
 * carries a scalar `total` (CLAUDE.md Pagination); later pages carry none.
 * `cursor` is opaque — Postgres' full-precision text, never a JS Date (bug 6).
 */
export function makeListHelpdeskTicketsQuery(_m: HM = enHelpdeskFormMessages) {
	return z.object({
		games_id: uuid,
		status: enumList(HELPDESK_STATUSES).optional(),
		category: enumList(HELPDESK_CATEGORIES).optional(),
		priority: enumList(HELPDESK_PRIORITIES).optional(),
		lodging_building_id: uuid.optional(),
		responsible_organization_id: uuid.optional(),
		assignee_user_id: uuid.optional(),
		q: z.string().trim().min(1).max(HELPDESK_SEARCH_MAX).optional(),
		sort: z.enum(HELPDESK_SORT_KEYS).default("newest"),
		cursor: z.string().min(1).max(HELPDESK_CURSOR_MAX).optional(),
		limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
	});
}
export const listHelpdeskTicketsQuery = makeListHelpdeskTicketsQuery();
export type ListHelpdeskTicketsQuery = z.output<typeof listHelpdeskTicketsQuery>;
export type ListHelpdeskTicketsQueryInput = z.input<typeof listHelpdeskTicketsQuery>;

/**
 * `POST /helpdesk/tickets` (S2). `source` is decided by the server from the
 * caller (DESK / NOC / HOTEL), never sent. Filed under the reporter's
 * predicate (`ticketReportAllowed`). ROOM ⇒ `lodging_building_id`; a room
 * needs its building.
 */
export function makeCreateHelpdeskTicketBody(m: HM = enHelpdeskFormMessages) {
	return z
		.object({
			games_id: uuid,
			category: z.enum(HELPDESK_CATEGORIES),
			priority: z.enum(HELPDESK_PRIORITIES).default(HELPDESK_PRIORITY_DEFAULT),
			title: z.string().trim().min(1, m.titleRequired).max(HELPDESK_TITLE_MAX, m.textTooLong),
			details: nullableText(m, HELPDESK_DETAILS_MAX),
			lodging_building_id: uuid.nullish(),
			lodging_room_id: uuid.nullish(),
			/** The typed room label ("412" or "A-412") when the room is not picked. */
			room_no: z.string().trim().min(1).max(LODGING_BLOCK_MAX + 1 + LODGING_ROOM_NO_MAX).nullish(),
			responsible_organization_id: uuid.nullish(),
			ac_card_id: uuid.nullish(),
			contact_name: nullableText(m, HELPDESK_CONTACT_NAME_MAX),
			contact_phone: nullableText(m, HELPDESK_CONTACT_PHONE_MAX),
		})
		.refine((v) => v.category !== "ROOM" || v.lodging_building_id != null, { message: m.buildingRequired, path: ["lodging_building_id"] })
		.refine((v) => v.lodging_room_id == null || v.lodging_building_id != null, { message: m.buildingRequired, path: ["lodging_building_id"] })
		.refine((v) => v.lodging_room_id == null || v.room_no == null, { message: m.roomBothGiven, path: ["room_no"] })
		.refine((v) => v.room_no == null || v.lodging_building_id != null, { message: m.buildingRequired, path: ["lodging_building_id"] });
}
export const createHelpdeskTicketBody = makeCreateHelpdeskTicketBody();
export type CreateHelpdeskTicketBody = z.input<typeof createHelpdeskTicketBody>;

/** `PATCH /helpdesk/tickets/:id` (S2) — title, priority, assignee (null = unassign). Status changes ride on a note. */
export function makePatchHelpdeskTicketBody(m: HM = enHelpdeskFormMessages) {
	return z
		.object({
			title: z.string().trim().min(1, m.titleRequired).max(HELPDESK_TITLE_MAX, m.textTooLong).optional(),
			priority: z.enum(HELPDESK_PRIORITIES).optional(),
			assignee_user_id: uuid.nullable().optional(),
		})
		.refine((v) => v.title !== undefined || v.priority !== undefined || v.assignee_user_id !== undefined, { message: m.nothingToChange });
}
export const patchHelpdeskTicketBody = makePatchHelpdeskTicketBody();
export type PatchHelpdeskTicketBody = z.input<typeof patchHelpdeskTicketBody>;

/**
 * `POST /helpdesk/tickets/:id/notes` (S2) — an append-only note; `status`
 * moves the ticket in the same transaction (HELPDESK_TRANSITIONS; the
 * trigger is the backstop → INVALID_STATUS_TRANSITION). `body` may be left
 * out when `status` is given (a status-only entry); at least one of the two
 * is required. Trimmed first; blank = absent (→ null).
 */
export function makeAddHelpdeskNoteBody(m: HM = enHelpdeskFormMessages) {
	return z
		.object({
			body: z
				.string()
				.trim()
				.max(HELPDESK_NOTE_MAX, m.textTooLong)
				.nullable()
				.optional()
				.transform((v) => (v === undefined || v === null || v === "" ? null : v)),
			status: z.enum(HELPDESK_STATUSES).optional(),
		})
		.refine((v) => v.body !== null || v.status !== undefined, { message: m.bodyRequired, path: ["body"] });
}
export const addHelpdeskNoteBody = makeAddHelpdeskNoteBody();
export type AddHelpdeskNoteBody = z.input<typeof addHelpdeskNoteBody>;

// ── Responses ───────────────────────────────────────────────────────────────

export interface HelpdeskUserRef {
	user_id: string;
	name: string;
}

/** One row of `GET /helpdesk/tickets`. A NOC sees only its own organisation's tickets. */
export interface HelpdeskTicketListItem {
	helpdesk_ticket_id: string;
	ticket_no: number;
	games_id: string;
	category: HelpdeskCategory;
	priority: HelpdeskPriority;
	status: HelpdeskStatus;
	source: HelpdeskSource;
	title: string;
	building: { lodging_building_id: string; name: string } | null;
	room: { lodging_room_id: string; room_label: string } | null;
	organization: { responsible_organization_id: string; code: string; name: string } | null;
	assignee: HelpdeskUserRef | null;
	created_by: HelpdeskUserRef | null;
	created_at: string;
	updated_at: string;
	resolved_at: string | null;
	note_count: number;
	/** This viewer may work the ticket (`ticketWritable`). */
	can_edit: boolean;
	last_note: { body: string | null; status_to: HelpdeskStatus | null; created_at: string; created_by_name: string | null } | null;
}

/** `GET /helpdesk/tickets`: keyset; `total` on the first page only (null after). */
export interface HelpdeskTicketPage extends Keyset<HelpdeskTicketListItem> {
	total: number | null;
}

export interface HelpdeskNote {
	helpdesk_ticket_note_id: string;
	/** null = a status-only entry (`status_to` set). */
	body: string | null;
	status_to: HelpdeskStatus | null;
	created_by: HelpdeskUserRef | null;
	created_at: string;
}

/** `GET /helpdesk/tickets/:id`: the row plus details and ≤ HELPDESK_NOTES_MAX notes (oldest first). */
export interface HelpdeskTicketDetail extends HelpdeskTicketListItem {
	details: string | null;
	contact_name: string | null;
	contact_phone: string | null;
	ac_card: { ac_card_id: string; name: string; accreditation_no: number | null } | null;
	notes: HelpdeskNote[];
	/** What this viewer may do (from `ticketWritable`): work the ticket, and the statuses it may move to. */
	can_handle: boolean;
	next_statuses: HelpdeskStatus[];
}
